import { ButtonV2, Input } from '@cimplico/components';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Controller, useForm } from 'react-hook-form';
import { toastNotification } from '../../../../components/toast/Toast';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import { useUpdateUsers } from '../../../../queries/use-update-users';
import { User } from '../../../../types/user';
import SectionLabel from '../../../../components/section-label/SectionLabel';
import SettingsContainer from '../../../../components/settings-container/SettingsContainer';

type PersonalInformationProps = {
    user: User;
};

type UserFormFields = {
    firstName: string;
    lastName: string;
};

function PersonalInformation({ user }: PersonalInformationProps): JSX.Element {
    const activeSubscription = useIsActiveSubscription();
    const updateUserQuery = useUpdateUsers();

    const {
        control,
        formState: { errors, isDirty, dirtyFields },
        handleSubmit,
        getValues,
    } = useForm<UserFormFields>({
        defaultValues: {
            firstName: user.firstName,
            lastName: user.lastName,
        },
    });

    function onSubmit(): void {
        const values = getValues();

        if (!isDirty) {
            return;
        }

        updateUserQuery.mutate(
            [
                {
                    id: user.id,
                    fields: {
                        firstName: dirtyFields.firstName ? values.firstName : undefined,
                        lastName: dirtyFields.lastName ? values.lastName : undefined,
                    },
                },
            ],
            {
                onSuccess: () => {
                    toastNotification('success', 'User details updated!');
                },
                onError: () => {
                    toastNotification('error', 'Unable to update user details!');
                },
            }
        );
    }

    return (
        <SettingsContainer>
            <SectionLabel
                title="Personal information"
                // description="Update your personal information, such as your name, email address and job title."
                description="Update your personal information, such as your first name and last name."
            />
            <form className="grid grid-cols-2 gap-8" onSubmit={handleSubmit(onSubmit)}>
                <Controller
                    name="firstName"
                    rules={{ required: 'First name is required.' }}
                    control={control}
                    render={({ field }): JSX.Element => (
                        <Input
                            disabled={!activeSubscription}
                            type="text"
                            label="First name"
                            size="small"
                            defaultValue={field.value}
                            isError={Boolean(errors.firstName)}
                            errorMessage={errors?.firstName?.message?.toString()}
                            onChange={(value): void => field.onChange(value)}
                        />
                    )}
                />
                <Controller
                    name="lastName"
                    rules={{ required: 'Last name is required.' }}
                    control={control}
                    render={({ field }): JSX.Element => (
                        <Input
                            disabled={!activeSubscription}
                            type="text"
                            label="Last name"
                            size="small"
                            defaultValue={field.value}
                            isError={Boolean(errors.lastName)}
                            errorMessage={errors?.lastName?.message?.toString()}
                            onChange={(value): void => field.onChange(value)}
                        />
                    )}
                />
                {/* <div className="col-span-2">
                    <Input type="text" label="Email" size="small" />
                </div>
                <div className="col-span-2">
                    <Input type="text" label="Job title" size="small" />
                </div> */}
                <div>
                    <ButtonV2
                        label="Save"
                        size="sm"
                        leftIcon={<CheckIcon className="text-white" />}
                        isDisabled={!activeSubscription}
                        isSubmit
                    />
                </div>
            </form>
        </SettingsContainer>
    );
}

export default PersonalInformation;
